import type {
  GridApiPremium,
  GridDataSource,
  GridGroupNode,
  GridRowId,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import objectHash from "object-hash";
import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ApiResponse, ListWithTotal, RangeRequestParams } from "../../api/types";
import type { AnyObject } from "../../types";
import { refetchGridDataSource, reloadGridDataSource, scrollGridToTop } from "../../utilities/dataGridApiHelper";
import { GRID_DEFAULT_PAGE_SIZE } from "./gridHelper";

/**
 * To be used for DataGrid when groups are expanded by default.
 * Returns a function to pass into isGroupExpandedByDefault prop.
 */
export const useGridGroupsExpanded = (apiRef: RefObject<GridApiPremium | null>) => {
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<GridRowId[]>([]);

  const isGroupExpanded = useCallback(
    (groupNode: GridGroupNode) => !collapsedGroupIds.includes(groupNode.id),
    [collapsedGroupIds]
  );

  useEffect(() => {
    return apiRef.current?.subscribeEvent("rowExpansionChange", ({ id, childrenExpanded }) => {
      setCollapsedGroupIds((ids) => (childrenExpanded ? ids.filter((nodeId) => nodeId !== id) : [...ids, id]));
    });
  }, [apiRef]);

  return { isGroupExpanded };
};

/**
 * To be used for DataGrid when groups are collapsed by default.
 * Returns a function to pass into isGroupExpandedByDefault prop.
 */
export const useGridGroupsCollapsed = (apiRef: RefObject<GridApiPremium | null>) => {
  const [expandedGroupIds, setExpandedGroupIds] = useState<GridRowId[]>([]);

  const isGroupExpanded = useCallback(
    (groupNode: GridGroupNode) => expandedGroupIds.includes(groupNode.id),
    [expandedGroupIds]
  );

  useEffect(() => {
    return apiRef.current?.subscribeEvent("rowExpansionChange", ({ id, childrenExpanded }) => {
      setExpandedGroupIds((ids) => (childrenExpanded ? [...ids, id] : ids.filter((nodeId) => nodeId !== id)));
    });
  }, [apiRef]);

  return { isGroupExpanded };
};

export const useGridDataSourceWithRange = <P extends AnyObject, R extends GridValidRowModel>(
  fetchRows: (p: P & { range: RangeRequestParams }) => Promise<ApiResponse<ListWithTotal<R>>>,
  payload: P,
  pageSize = GRID_DEFAULT_PAGE_SIZE
) => {
  const gridApiRef = useGridApiRef();

  const [totalRows, setTotalRows] = useState<number>();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const payloadHash = objectHash(payload, { unorderedArrays: true });
  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: async (params) => {
        const skip = Number(params.start);
        const requestedTake = params.end - skip + 1;
        const range = { skip, take: requestedTake > 0 ? requestedTake : pageSize, totals: skip === 0 };

        const response = await fetchRows({ range, ...payload });
        const rowCount = response.data?.total;
        if (rowCount !== undefined) {
          setTotalRows(rowCount);
        }

        return { rows: response.data?.items ?? [], rowCount };
      },
    }),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [payloadHash, pageSize]
  );

  const refreshDataSource = useCallback(async () => {
    setIsRefreshing(true);
    scrollGridToTop(gridApiRef);
    try {
      await refetchGridDataSource(gridApiRef);
    } finally {
      setIsRefreshing(false);
    }
  }, [gridApiRef]);

  const reloadDataSource = useCallback(() => reloadGridDataSource(gridApiRef, pageSize), [gridApiRef, pageSize]);

  return {
    gridApiRef,
    dataSource,
    totalRows,
    isLoading: totalRows === undefined || isRefreshing,
    isRefreshing,
    refreshDataSource,
    reloadDataSource,
  };
};
